import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { get } from 'lodash';
import tabs from '@/components/generics/tabs/tabs.vue';
import ls from '@/helpers/localStorage';
import loaderMixin from '@/mixins/loader';
import { hasMarketHiddenContracts } from '@/utils/helpers';
import log from '@/logger';
import smallModal from '@/components/generics/modal/small/small.vue';

const MARKET_OPEN_DAYS_TWENTY_HOUR_CHART = 2;
export const TAB_SEVEN_DAYS = '7d';
export const TAB_THIRTY_DAYS = '30d';
export const TAB_NINETY_DAYS = '90d';
export const TAB_TWENTY_FOUR_HOURS = '24h';
export const TAB_RANGE = '0';
export const TAB_STATS = 'stats';

const TAB_LINE = 'line';
const TAB_CANDLE = 'candle';

const LS_SHOW_VOLUME_CHART = 'showVolumeChart';
const LS_SHOW_PRICE_AXIS_ZOOM = 'showPriceAxisZoom';

const UNIT_OF_TIME_MAP = {
  1: 'h',
  2: 'd',
};

export default {
  mixins: [
    loaderMixin,
  ],
  components: {
    tabs,
    smallModal,
  },
  props: {
    market: {
      type: Object,
      required: true,
    },
    chartOptions: {
      type: Object,
      default: null,
    },
    showVolume: {
      type: Boolean,
      default: false,
    },
    showPriceAxisZoom: {
      type: Boolean,
      default: true,
    },
    isChartLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showVolumeChart: ls.get(LS_SHOW_VOLUME_CHART, this.showVolume),
      showPriceAxisZoomValue: ls.get(LS_SHOW_PRICE_AXIS_ZOOM, this.showPriceAxisZoom),
      showMenu: false,
      activeTabPeriod: null,
      activeTabType: null,
    };
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    isForStory() {
      return !!get(this.chartOptions, 'cmsStoryId', null);
    },
    tabsPeriodStyleModifiers() {
      return {};
    },
    /*
    *  Determines which timeframe mode to use. A chart can show a timeframe
    *  in one of two ways:
    *    1) Date Range - data between a start date and end date
    *    2) To Date: data from the last X days/hours to the current date
    */
    useDateRange() {
      return get(this.chartOptions, 'useDateRange', false);
    },
    /*
    * "To Date" is a timeframe that shows the last X days/hours of data and is
    * used if not using the "Date Range" timeframe. Each option is formatted
    * {
    *   unit: Number (1 for hour, 2 for days)
    *   quantity: Number (integer representing how many days/hours)
    *   isDefault: Boolean (whether this option should be the default option)
    * }
    * These options will become the period tabs.
    */
    toDateOptions() {
      return get(this.chartOptions, 'toDateAvailableOptions');
    },
    dateRangeUnit() {
      return get(this.chartOptions, 'dateRangeUnit');
    },
    showDateRangeTab() {
      return this.useDateRange
        && get(this.chartOptions, 'startDate', null) !== null
        && get(this.chartOptions, 'endDate', null) !== null;
    },
    showToDateTab() {
      return !this.useDateRange && this.toDateOptions && this.toDateOptions.length > 0;
    },
    defaultRangeTab() {
      const suffix = this.dateRangeUnit ? UNIT_OF_TIME_MAP[this.dateRangeUnit] : 'd';
      return { name: `${TAB_RANGE}${suffix}`, title: null, icon: 'calendar' };
    },
    defaultToDateTab() {
      const defaultToDateOption = this.showToDateTab ?
        this.toDateOptions.find(option => option.isDefault) || this.toDateOptions[0] :
        null;
      return this.tabFromToDateOption(defaultToDateOption);
    },
    tabsPeriod() {
      const items = [];

      if (this.chartOptions === null || get(this.chartOptions, 'include24hr', false)) {
        items.push({ name: TAB_TWENTY_FOUR_HOURS, title: '24h' });
      }
      if (this.chartOptions === null || get(this.chartOptions, 'include7day', false)) {
        items.push({ name: TAB_SEVEN_DAYS, title: '7d' });
      }
      if (this.chartOptions === null || get(this.chartOptions, 'include30day', false)) {
        items.push({ name: TAB_THIRTY_DAYS, title: '30d' });
      }
      if (this.chartOptions === null || get(this.chartOptions, 'include90day', false)) {
        items.push({ name: TAB_NINETY_DAYS, title: '90d' });
      }

      if (this.showDateRangeTab) {
        items.push(this.defaultRangeTab);
      } else if (this.showToDateTab) {
        this.toDateOptions.forEach(option => items.push(this.tabFromToDateOption(option)));
      }
      return items;
    },
    tabsType() {
      const items = [];
      if (this.chartOptions === null || get(this.chartOptions, 'includeCandle', false)) {
        items.push({ name: TAB_CANDLE, title: 'Candle' });
      }
      if (this.chartOptions === null || get(this.chartOptions, 'includeLine', false)) {
        items.push({ name: TAB_LINE, title: 'Line' });
      }
      return items;
    },
  },
  methods: {
    ...mapActions('popup', ['setGenericPopup']),
    ...mapActions('market', ['setChartTimeline', 'getMarketContractsStats', 'setChartType']),
    onOpenSettings() {
      this.showMenu = !this.showMenu;
    },
    onClickOutside() {
      this.showMenu = false;
    },
    onOpenStats() {
      if (this.isChartLoading) {
        return;
      }
      this.loaderAdd(document.body);
      this.showMenu = false;
      this.getMarketContractsStats(this.market.marketId)
        .then((data) => {
          this.loaderRemove(document.body);
          this.setGenericPopup({
            name: 'market-contracts-stats',
            options: {
              stats: data,
              isFixed: hasMarketHiddenContracts(this.market),
            },
          });
        })
        .catch(() => this.loaderRemove(document.body));
    },
    tabChangePeriod(name) {
      if (this.isChartLoading) {
        return;
      }
      log.info(`[CHART][TIMELINE] tabChangePeriod to ${name} for ${this.market.marketId}`);
      this.activeTabPeriod = name;
      this.setChartTimeline(name);
      this.$emit('timelineChange', this.activeTabPeriod);
    },
    tabChangeType(name) {
      if (this.isChartLoading) {
        return;
      }
      log.info(`[CHART][TIMELINE] tabChangeType to ${name} for ${this.market.marketId}`);
      this.activeTabType = name;
      this.setChartType(name);
      this.$emit('typeChange', this.activeTabType);
    },
    tabsPeriodHandler(items) {
      const dateOpenedDayDiff = moment().diff(moment(this.market.dateOpened), 'days');
      let activeTabPeriod = (dateOpenedDayDiff < MARKET_OPEN_DAYS_TWENTY_HOUR_CHART) ?
        TAB_TWENTY_FOUR_HOURS :
        TAB_SEVEN_DAYS;
      if (this.showDateRangeTab) {
        activeTabPeriod = this.defaultRangeTab.name;
      } else if (this.showToDateTab) {
        activeTabPeriod = this.defaultToDateTab.name;
      }

      const activeTab = items.find(tab => tab.name === activeTabPeriod);
      if (activeTab) {
        this.activeTabPeriod = activeTabPeriod;
      } else if (items.length > 0) {
        this.activeTabPeriod = items[0].name;
      }
    },
    tabsTypeHandler(value) {
      if (!value || value.length === 0) return;
      if (value.find(i => i.name === TAB_LINE)) {
        this.activeTabType = TAB_LINE;
      } else {
        this.activeTabType = value[0].name;
      }
    },
    tabFromToDateOption(toDateOption) {
      if (toDateOption) {
        const name = `${toDateOption.quantity}${UNIT_OF_TIME_MAP[toDateOption.unit]}`;
        const title = name;
        return { name, title };
      }
      return {};
    },
    onVolumeChartToggle() {
      if (this.isChartLoading) {
        return;
      }
      this.showVolumeChart = !this.showVolumeChart;
      ls.set(LS_SHOW_VOLUME_CHART, this.showVolumeChart);
      this.$emit('volumeChartToggle', this.showVolumeChart);
    },
    onPriceAxisZoomToggle() {
      if (this.isChartLoading) {
        return;
      }
      this.showPriceAxisZoomValue = !this.showPriceAxisZoomValue;
      ls.set(LS_SHOW_PRICE_AXIS_ZOOM, this.showPriceAxisZoomValue);
      this.$emit('priceAxisZoomToggle', this.showPriceAxisZoomValue);
    },
  },
  watch: {
    tabsPeriod: {
      handler: 'tabsPeriodHandler',
      immediate: true,
    },
    tabsType: {
      handler: 'tabsTypeHandler',
      immediate: true,
    },
  },
  mounted() {
    this.$emit('volumeChartToggle', this.showVolumeChart);
    this.$emit('priceAxisZoomToggle', this.showPriceAxisZoomValue);
    log.info(`[CHART][TIMELINE] got ${this.tabsPeriod.length} period tabs and ${this.tabsType.length} type tabs for ${this.market.marketId}`);
  },
};



// WEBPACK FOOTER //
// ./src/components/market/market-detail-charts/timeline/timeline.js